import "./PermissionPresets.scss";
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Checkbox, Input, Modal, Skeleton } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { env } from "../../../../env/env";
import { memberState } from "../../../../state/atoms";
import { PermissionPresetDto } from "../../../../generated/dashboard-client";
import PermissionPresetsApi from "../../../../api/PermissionPresetsApi";
import { success } from "../../../../service/MessageService";
import { handleServerError } from "../../../../util/ErrorHandler";
import ControlIcon from "../../../icons/ControlIcon";

type PermissionKey = "viewOnly" | "dataOnly" | "manageDesigns" | "createTemplates" | "print";

interface PermissionDef {
  key: PermissionKey;
  label: string;
  pill: string;
  description: string;
  requires?: PermissionKey;
}

const BASE_LEVELS: PermissionKey[] = ["viewOnly", "dataOnly", "manageDesigns"];

const PERMISSIONS: PermissionDef[] = [
  { key: "viewOnly", label: "View Only", pill: "View Only", description: "Users can view existing designs but cannot edit, download, or publish." },
  { key: "dataOnly", label: "Data Only", pill: "Data Only", description: "Users can only update menu data on existing templates, make small spacing adjustments, and download the final output." },
  { key: "manageDesigns", label: "Manage Designs", pill: "Manage designs", description: "Users can edit existing designs, download files, publish updates, and order professional prints." },
  { key: "createTemplates", label: "Create New Templates", pill: "Create templates", description: "Users can create new templates from scratch or from existing designs. Requires Manage Designs.", requires: "manageDesigns" },
  { key: "print", label: "Can Order Print", pill: "Print", description: "Allows user to place print orders from within MustHaveMenus. Requires Manage designs.", requires: "manageDesigns" },
];

// The list card shows a short one-line summary derived from the selected permissions.
function summarize(permissions: PermissionKey[]): string {
  if (permissions.includes("viewOnly")) return "Can view and download";
  if (permissions.includes("dataOnly")) return "Update menu data, download output";
  const parts: string[] = [];
  if (permissions.includes("manageDesigns")) parts.push("Edit");
  if (permissions.includes("createTemplates")) parts.push("create templates");
  if (permissions.includes("print")) parts.push("order print");
  return parts.join(", ");
}

interface Preset {
  id: string;
  name: string;
  permissions: PermissionKey[];
  isDefault: boolean;
}

// The DTO stores one boolean per permission; the frontend "print" key maps to the DTO "orderPrint" flag.
function fromDto(dto: PermissionPresetDto): Preset {
  const flags: Record<PermissionKey, boolean> = {
    viewOnly: !!dto.viewOnly,
    dataOnly: !!dto.dataOnly,
    manageDesigns: !!dto.manageDesigns,
    createTemplates: !!dto.createTemplates,
    print: !!dto.orderPrint,
  };
  const permissions = PERMISSIONS.map(p => p.key).filter(key => flags[key]);
  return { id: dto.id ?? "", name: dto.name ?? "", permissions, isDefault: !!dto.default };
}

function toDto(name: string, selected: PermissionKey[], isDefault: boolean): PermissionPresetDto {
  return {
    name,
    default: isDefault,
    viewOnly: selected.includes("viewOnly"),
    dataOnly: selected.includes("dataOnly"),
    manageDesigns: selected.includes("manageDesigns"),
    createTemplates: selected.includes("createTemplates"),
    orderPrint: selected.includes("print"),
  };
}

export function PermissionPresets() {
  const navigate = useNavigate();
  const [member] = useRecoilState(memberState);
  const teamId = member.teamId ?? "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [makingDefaultId, setMakingDefaultId] = useState<string | null>(null);
  const [presets, setPresets] = useState<Preset[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<PermissionKey[]>([]);

  const pillFor = useMemo(() => {
    const map: Record<string, string> = {};
    PERMISSIONS.forEach(p => (map[p.key] = p.pill));
    return map;
  }, []);

  const fetchPresets = useCallback(() => {
    if (!teamId) return;
    setLoading(true);
    PermissionPresetsApi.getPresets(teamId)
      .then(resp => setPresets(resp.data.map(fromDto)))
      .catch(handleServerError)
      .finally(() => setLoading(false));
  }, [teamId]);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  function openNew() {
    setEditingId(null);
    setName("");
    setSelected([]);
    setModalOpen(true);
  }

  function openEdit(preset: Preset) {
    setEditingId(preset.id);
    setName(preset.name);
    setSelected(preset.permissions);
    setModalOpen(true);
  }

  function toggle(key: PermissionKey, checked: boolean) {
    setSelected(prev => {
      let next = new Set(prev);
      if (checked) {
        next.add(key);
        // Base levels are mutually exclusive.
        if (BASE_LEVELS.includes(key)) {
          BASE_LEVELS.filter(l => l !== key).forEach(l => next.delete(l));
        }
      } else {
        next.delete(key);
        if (key === "manageDesigns") {
          PERMISSIONS.filter(p => p.requires === "manageDesigns").forEach(p => next.delete(p.key));
        }
      }
      return PERMISSIONS.map(p => p.key).filter(k => next.has(k));
    });
  }

  function isDisabled(perm: PermissionDef) {
    return !!perm.requires && !selected.includes(perm.requires);
  }

  function onSave() {
    const trimmed = name.trim();
    if (!trimmed || selected.length === 0 || !teamId) return;

    const editing = editingId ? presets.find(p => p.id === editingId) : null;
    const dto = toDto(trimmed, selected, editing?.isDefault ?? false);

    setSaving(true);
    const request = editingId
      ? PermissionPresetsApi.updatePreset(teamId, editingId, dto)
      : PermissionPresetsApi.createPreset(teamId, dto);

    request
      .then(() => {
        success(editingId ? "Preset updated" : "Preset created");
        setModalOpen(false);
        fetchPresets();
      })
      .catch(handleServerError)
      .finally(() => setSaving(false));
  }

  function onMakeDefault(preset: Preset) {
    if (!teamId) return;
    const dto = toDto(preset.name, preset.permissions, true);
    setMakingDefaultId(preset.id);
    PermissionPresetsApi.updatePreset(teamId, preset.id, dto)
      .then(() => {
        success("Default preset updated");
        // The backend enforces a single default per team, so mirror that locally:
        // mark this preset default and clear the rest, without refetching the list.
        setPresets(prev => prev.map(p => ({ ...p, isDefault: p.id === preset.id })));
      })
      .catch(handleServerError)
      .finally(() => setMakingDefaultId(null));
  }

  function onDelete(preset: Preset) {
    Modal.confirm({
      title: "Delete preset?",
      content: `Are you sure you want to delete "${preset.name}"? This can't be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk() {
        if (!teamId) return;
        return PermissionPresetsApi.deletePreset(teamId, preset.id)
          .then(() => {
            success("Preset deleted");
            fetchPresets();
          })
          .catch(handleServerError);
      },
    });
  }

  return (
    <div className={"content permission-presets"}>
      <a className={"back"} onClick={() => navigate(env.dashboardPath + "/account/team")}>
        <ArrowLeftOutlined /> Back to team
      </a>

      <header>
        <div className={"heading"}>
          <h3>Permission Presets</h3>
          <p>Reusable bundles you can reference in CSV imports or apply when adding users manually.</p>
        </div>
        {!loading && presets.length > 0 &&
          <Button type={"primary"} size={"large"} className={"new"} onClick={openNew}>+ New preset</Button>}
      </header>

      {loading && <Skeleton />}

      {!loading && presets.length === 0 &&
        <div className={"empty-state"}>
          <div className={"empty-icon"}><ControlIcon /></div>
          <h4>No presets yet</h4>
          <p>Create your first preset to get started.</p>
          <Button type={"primary"} size={"large"} className={"new"} onClick={openNew}>+ New Preset</Button>
        </div>}

      {!loading && presets.length > 0 &&
        <div className={"preset-list"}>
          {presets.map(preset => (
            <div className={`preset-card${preset.isDefault ? " default" : ""}`} key={preset.id}>
              <div className={"info"}>
                <div className={"name"}>{preset.name}</div>
                <div className={"summary"}>{summarize(preset.permissions)}</div>
                <div className={"pills"}>
                  {preset.permissions.map(perm => (
                    <span className={"pill"} key={perm}>{pillFor[perm]}</span>
                  ))}
                </div>
              </div>
              <div className={"actions"}>
                {preset.isDefault
                  ? <span className={"default-label"}>Default</span>
                  : <Button className={"make-default"} loading={makingDefaultId === preset.id} onClick={() => onMakeDefault(preset)}>Make default</Button>}
                <button className={"icon-btn"} onClick={() => openEdit(preset)} aria-label={"Edit preset"}>
                  <EditOutlined />
                </button>
                <button
                  className={"icon-btn"}
                  onClick={() => onDelete(preset)}
                  disabled={preset.isDefault}
                  aria-label={"Delete preset"}
                >
                  <DeleteOutlined />
                </button>
              </div>
            </div>
          ))}
        </div>}

      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={650}
        className={"preset-modal"}
      >
        <h4>{editingId ? "Edit preset" : "New preset"}</h4>
        <p className={"subtitle"}>Select permissions for this preset. Rules are enforced automatically.</p>

        <Input
          className={"name-input"}
          placeholder={"Preset name"}
          value={name}
          onChange={ev => setName(ev.target.value)}
        />

        <div className={"perm-options"}>
          {PERMISSIONS.map(perm => {
            const disabled = isDisabled(perm);
            return (
              <label className={`perm-option${disabled ? " disabled" : ""}`} key={perm.key}>
                <Checkbox
                  checked={selected.includes(perm.key)}
                  disabled={disabled}
                  onChange={ev => toggle(perm.key, ev.target.checked)}
                />
                <div className={"text"}>
                  <div className={"label"}>{perm.label}</div>
                  <div className={"desc"}>{perm.description}</div>
                </div>
              </label>
            );
          })}
        </div>

        {selected.includes("viewOnly") &&
          <div className={"perm-warning"}>
            <ExclamationCircleOutlined /> View only can't be combined with other permissions.
          </div>}

        {selected.includes("dataOnly") &&
          <div className={"perm-warning"}>
            <ExclamationCircleOutlined /> Data only can't be combined with other permissions.
          </div>}

        <div className={"modal-actions"}>
          <Button className={"cancel"} onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button type={"primary"} className={"save"} loading={saving} onClick={onSave} disabled={!name.trim() || selected.length === 0}>
            Save Preset
          </Button>
        </div>
      </Modal>
    </div>
  );
}
